"use client"

import { useState, useEffect, useCallback, JSX } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import {
  getDiskUsage,
  getDiskUsageMetadata,
  type DiskUsageMetadataResponse,
  type DiskUsageResponse,
  type DiskUsageScope,
  type DiskUsageItem,
} from "@/services/resourceUsageService"
import { House } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import {
  formatBytes,
  formatSnapshotDate,
  estimateWeeklyCost,
} from "./diskUsageUtils"
import DiskUsageTableBody from "./DiskUsageTableBody"
import type { DiskUsageRow } from "./DiskUsageTableBody"
import { Button } from "@/components/ui/button"

const PAGE_TITLE = "My Disk Usage"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Navigation state for the per-user disk usage view.
 *
 * Levels: virtual root (scope list) → directory tree within a scope.
 */
interface NavState {
  /** The currently active scope, or null when at the virtual root (scope list). */
  scope: DiskUsageScope | null
  /** The current path within the scope, or null when at the scope root. */
  path: string | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * DiskUsageTable component.
 *
 * Per-user disk usage viewer. Shows:
 * 1. Virtual root: each available scope with its total size.
 * 2. Scope level: standard directory tree navigation (unlimited depth).
 *
 * Cost estimates are displayed as weekly figures.
 */
export default function DiskUsageTable(): JSX.Element {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()

  // Metadata for the authenticated user (available scopes + latest versions)
  const [metadata, setMetadata] = useState<DiskUsageMetadataResponse | null>(null)

  // Navigation state
  const [nav, setNav] = useState<NavState>({ scope: null, path: null })

  // Scope root sizes: { scope: cumdsize } — null while still loading
  const [scopeRoots, setScopeRoots] = useState<Record<string, number | null>>({})

  // Current directory data (when inside a scope's directory tree)
  const [currentData, setCurrentData] = useState<DiskUsageResponse | null>(null)

  // Loading states
  const [initialLoading, setInitialLoading] = useState<boolean>(true)
  const [navigating, setNavigating] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  /**
   * Fetch metadata on mount and then load each scope's root size concurrently.
   * Scope sizes are loaded progressively — each resolves independently so the
   * table populates as results arrive.
   */
  useEffect(() => {
    async function init(): Promise<void> {
      try {
        const accessToken = await getAccessToken()
        const meta = await getDiskUsageMetadata(accessToken)
        setMetadata(meta)

        if (meta.scopes_available.length === 0) {
          setInitialLoading(false)
          return
        }

        // Mark all scopes as loading
        const initRoots: Record<string, number | null> = {}
        for (const scope of meta.scopes_available) {
          initRoots[scope] = null
        }
        setScopeRoots(initRoots)

        // Fetch each scope root in parallel; update state as each one resolves
        for (const scope of meta.scopes_available) {
          const version = meta.latest[scope]
          if (!version) {
            setScopeRoots(prev => ({ ...prev, [scope]: 0 }))
            continue
          }

          getDiskUsage({ scope, version }, accessToken)
            .then(data => {
              setScopeRoots(prev => ({ ...prev, [scope]: data.cumdsize }))
            })
            .catch(() => {
              setScopeRoots(prev => ({ ...prev, [scope]: 0 }))
            })
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load data"
        setError(message)
      } finally {
        setInitialLoading(false)
      }
    }

    init()
  }, [getAccessToken])

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  /**
   * Navigate to a specific path within a scope and load its directory contents.
   *
   * @param scope - The scope to navigate within.
   * @param path - The path to navigate to (null means the scope root).
   */
  const navigateToPath = useCallback(
    async (scope: DiskUsageScope, path: string | null): Promise<void> => {
      if (!metadata) return

      const version = metadata.latest[scope]
      if (!version) return

      setNavigating(true)
      setError(null)

      try {
        const accessToken = await getAccessToken()
        const data = await getDiskUsage(
          {
            scope,
            version,
            path: path ?? undefined,
          },
          accessToken
        )
        setCurrentData(data)
        setNav({ scope, path })
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load directory"
        setError(message)
      } finally {
        setNavigating(false)
      }
    },
    [metadata, getAccessToken]
  )

  /**
   * Handle clicking a directory row inside a scope's directory tree.
   *
   * @param itemPath - The path of the directory that was clicked.
   */
  function handleDirectoryClick(itemPath: string): void {
    if (!nav.scope) return
    navigateToPath(nav.scope, itemPath)
  }

  /**
   * Navigate up one level.
   * - From a subdirectory: go to the parent directory.
   * - From the scope root: go back to the virtual root (scope list).
   */
  function navigateUp(): void {
    if (nav.scope === null) return // Already at virtual root

    if (nav.path === null || nav.path === "/") {
      // At scope root → back to virtual root
      setNav({ scope: null, path: null })
      setCurrentData(null)
    } else {
      // Go to parent path within the scope tree
      const parts = nav.path.replace(/^\//, "").split("/")
      parts.pop()
      const parentPath = parts.length === 0 ? null : "/" + parts.join("/")
      navigateToPath(nav.scope, parentPath)
    }
  }

  // ---------------------------------------------------------------------------
  // Build rows
  // ---------------------------------------------------------------------------

  /**
   * Derive the table rows from the current navigation state.
   *
   * At the virtual root this returns one row per available scope.
   * Inside a scope it returns one row per item in currentData.contents.
   *
   * @returns An array of DiskUsageRow objects ready to pass to DiskUsageTableBody.
   */
  function buildRows(): DiskUsageRow[] {
    if (nav.scope === null) {
      // Virtual root: one row per scope
      if (!metadata) return []
      return metadata.scopes_available.map(scope => ({
        name: scope,
        isDirectory: true,
        size: scopeRoots[scope] ?? 0,
        loading: scopeRoots[scope] === null,
        scope,
        navigatePath: null,
      }))
    }

    // Inside a scope: rows from currentData
    if (!currentData) return []

    return currentData.contents.map((item: DiskUsageItem) => {
      const size = item.isDirectory ? (item.cumdsize ?? 0) : item.dsize
      const itemPath =
        currentData.path === "/" ? `/${item.name}` : `${currentData.path}/${item.name}`

      return {
        name: item.name,
        isDirectory: item.isDirectory,
        size,
        loading: false,
        scope: nav.scope,
        navigatePath: itemPath,
      }
    })
  }

  const rows = buildRows()
  const sortedRows = [...rows].sort((a, b) => b.size - a.size)
  const maxSize = sortedRows.reduce((sum, row) => sum + row.size, 0) || 1

  // Whether the user is currently at the virtual root (scope list)
  const isAtVirtualRoot = nav.scope === null

  // ---------------------------------------------------------------------------
  // Breadcrumbs
  // ---------------------------------------------------------------------------

  /**
   * Build clickable breadcrumb segments from the current navigation state.
   *
   * @returns An array of breadcrumb objects, each with a label and onClick handler.
   */
  function buildBreadcrumbs(): { label: string; onClick: () => void; isRoot?: boolean }[] {
    const crumbs: { label: string; onClick: () => void; isRoot?: boolean }[] = []

    // Root crumb always present
    crumbs.push({
      label: "__root__",
      isRoot: true,
      onClick: () => {
        setNav({ scope: null, path: null })
        setCurrentData(null)
      },
    })

    if (nav.scope === null) return crumbs

    // Scope crumb
    crumbs.push({
      label: nav.scope,
      onClick: () => navigateToPath(nav.scope!, null),
    })

    // Path segment crumbs
    if (nav.path && nav.path !== "/") {
      const parts = nav.path.replace(/^\//, "").split("/")
      for (let i = 0; i < parts.length; i++) {
        const partPath = "/" + parts.slice(0, i + 1).join("/")
        const part = parts[i]
        crumbs.push({
          label: part,
          onClick: () => navigateToPath(nav.scope!, partPath),
        })
      }
    }

    return crumbs
  }

  const breadcrumbs = buildBreadcrumbs()

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (error && !metadata) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    )
  }

  if (metadata && metadata.scopes_available.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        <p className="text-lg font-semibold">No Data Available</p>
        <p className="text-sm text-muted-foreground">No disk usage data found for your account.</p>
      </div>
    )
  }

  // Derive the snapshot label shown in the card description
  const snapshotVersion = nav.scope
    ? metadata?.latest[nav.scope]
    : metadata
      ? Object.values(metadata.latest).find(Boolean)
      : null

  // Compute the total across all scopes for the description (only shown at virtual root)
  const grandTotalBytes = isAtVirtualRoot
    ? Object.values(scopeRoots).reduce<number>((sum, v) => sum + (v ?? 0), 0)
    : null

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <Card className="flex flex-1 flex-col min-h-0 shadow-none gap-4">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <h1>{PAGE_TITLE}</h1>
            </CardTitle>
            <CardDescription>
              <p className="text-sm text-muted-foreground mt-1">
                {grandTotalBytes !== null && (
                  <>Total: {formatBytes(grandTotalBytes)} · </>
                )}
                Weekly cost estimates
                {snapshotVersion ? <> · Snapshot: {formatSnapshotDate(snapshotVersion)}</> : null}
              </p>
            </CardDescription>
          </div>
          {/* Show "Admin View" button when the user has admin access */}
          {metadata?.users_available && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/computing/resource-usage/disk/admin")}
            >
              Admin View
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col min-h-0 gap-2">
        {/* Breadcrumb navigation */}
        <div>
          <p className="text-sm font-mono flex flex-wrap items-end gap-0.5 leading-none">
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1
              const display = crumb.isRoot ? <House className="h-4 w-4" /> : crumb.label
              return (
                <span key={i} className="flex items-center gap-0.5">
                  {i > 0 && <span className="text-muted-foreground">/</span>}
                  {isLast ? (
                    <span className="text-foreground">{display}</span>
                  ) : (
                    <button onClick={crumb.onClick} className="text-primary hover:underline">
                      {display}
                    </button>
                  )}
                </span>
              )
            })}
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Disk usage table */}
        <DiskUsageTableBody
          rows={sortedRows}
          maxSize={maxSize}
          initialLoading={initialLoading}
          navigating={navigating}
          isAtRoot={isAtVirtualRoot}
          costColumnLabel="Weekly Cost"
          estimateCost={estimateWeeklyCost}
          onNavigateUp={navigateUp}
          onRowClick={(row: DiskUsageRow) => {
            if (nav.scope === null && row.scope) {
              // At virtual root: navigate into the clicked scope
              navigateToPath(row.scope, null)
            } else if (row.navigatePath) {
              // Inside a scope: navigate into the clicked directory
              handleDirectoryClick(row.navigatePath)
            }
          }}
        />
      </CardContent>
    </Card>
    </>
  )
}
